import React from 'react';
import { ArrowDownLeft, Download } from 'lucide-react';

interface InventoryItemSummaryReportProps {
  endDate: string;
  items: any[];
  setActiveReport: (report: string | null) => void;
  handleExport: () => void;
}

export const InventoryItemSummaryReport: React.FC<InventoryItemSummaryReportProps> = ({
  endDate,
  items,
  setActiveReport,
  handleExport
}) => {
  return (
    <div className="space-y-8 text-foreground">
      <div className="flex items-center justify-between">
        <button 
          onClick={() => setActiveReport(null)}
          className="flex items-center gap-2 text-sm font-bold hover:opacity-70 transition-opacity"
        >
          <ArrowDownLeft className="w-4 h-4 rotate-45" />
          <span>Back to Reports</span>
        </button>
        <div className="flex items-center gap-3">
          <button 
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-[#141414] text-[#E4E3E0] rounded-full text-xs font-bold hover:opacity-90 transition-all"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      <div className="bg-white/50 dark:bg-white/5 border border-[#141414] dark:border-white/10 rounded-[40px] p-12 shadow-sm">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold tracking-tighter italic serif mb-2">Inventory Item Summary</h1>
          <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40">As of {new Date(endDate).toLocaleDateString()}</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-[#141414] dark:border-white/20">
                <th className="p-4 text-left text-[10px] uppercase font-bold tracking-widest opacity-40">Item</th>
                <th className="p-4 text-left text-[10px] uppercase font-bold tracking-widest opacity-40">Warehouse</th>
                <th className="p-4 text-left text-[10px] uppercase font-bold tracking-widest opacity-40">Lot Number</th>
                <th className="p-4 text-left text-[10px] uppercase font-bold tracking-widest opacity-40">Expiry</th>
                <th className="p-4 text-right text-[10px] uppercase font-bold tracking-widest opacity-40">Qty on Hand</th>
                <th className="p-4 text-right text-[10px] uppercase font-bold tracking-widest opacity-40">Unit Cost</th>
                <th className="p-4 text-right text-[10px] uppercase font-bold tracking-widest opacity-40">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#141414]/5 dark:divide-white/5">
              {items.flatMap(item => (item.stockRecords || []).map((sr: any) => ({
                ...sr,
                itemName: item.name,
                itemCode: item.code,
                baseCost: item.costPrice || 0
              })))
              .sort((a, b) => a.itemName.localeCompare(b.itemName))
              .map((record, idx) => (
                <tr key={idx} className="hover:bg-[#141414]/5 dark:hover:bg-white/5 transition-colors">
                  <td className="p-4">
                    <p className="text-xs font-bold">{record.itemName}</p>
                    <p className="text-[9px] opacity-40 font-mono">{record.itemCode}</p>
                  </td>
                  <td className="p-4 text-xs opacity-60 font-bold">{record.warehouseName || record.warehouseId}</td>
                  <td className="p-4 text-xs font-mono">{record.lotNumber || '-'}</td>
                  <td className="p-4 text-xs font-mono">{record.expiryDate || '-'}</td>
                  <td className="p-4 text-right font-mono text-xs font-bold">{record.quantity && record.quantity.toLocaleString()}</td>
                  <td className="p-4 text-right font-mono text-xs">{(record.unitCost || record.baseCost).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="p-4 text-right font-mono text-xs font-bold">{(record.quantity * (record.unitCost || record.baseCost)).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-12 text-center opacity-40 italic">No inventory data available</td>
                </tr>
              )}
            </tbody>
            {items.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-[#141414] dark:border-white/20 font-bold">
                  <td colSpan={6} className="p-4 text-right text-[10px] uppercase tracking-widest opacity-40">Total Inventory Value</td>
                  <td className="p-4 text-right font-mono text-xs">
                    {items.reduce((total, item) => {
                      return total + (item.stockRecords || []).reduce((sum: number, sr: any) => sum + (sr.quantity * (sr.unitCost || item.costPrice || 0)), 0);
                    }, 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};
